import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { resolveImg } from "@/lib/api";
import { OPTIONS, INTENTS, CITIES, AVATARS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { DoorOpen, UserPlus, Users, Camera, Loader2, ChevronRight, Check } from "lucide-react";

const IconMap = { DoorOpen, UserPlus, Users };

function Chip({ active, onClick, children, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`rounded-full border px-4 py-2 text-sm font-semibold transition-all active:scale-95 ${active ? "border-brand bg-brand text-white shadow-brand" : "border-border bg-white text-ink-soft"}`}
    >
      {children}
    </button>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm font-semibold text-ink">{label}</Label>
      {children}
    </div>
  );
}

export default function Onboarding() {
  const { user, updateUser } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [intents, setIntents] = useState(user?.intents || []);
  const [p, setP] = useState({
    first_name: user?.profile?.first_name || (user?.name || "").split(" ")[0] || "",
    last_name: user?.profile?.last_name || "",
    age: user?.profile?.age || "",
    gender: user?.profile?.gender || "",
    employment_status: "working",
    sector: "technology",
    role: user?.profile?.role || user?.profile?.occupation || "",
    work_mode: "hybrid",
    occupation: user?.profile?.occupation || "",
    company_college: user?.profile?.company_college || "",
    city: user?.profile?.city || "",
    bio: user?.profile?.bio || "",
    photo_url: user?.profile?.photo_url || "",
    wake_time: "normal", sleep_time: "normal",
    personal_food: "veg", food_comfortable: ["veg", "nonveg"],
    smoking: "no", drinking: "occasionally", pets: "ok", cleanliness: 4,
    social_lifestyle: "ambivert", noise_preference: "moderate", party_frequency: "sometimes", guest_preference: "sometimes",
    work_schedule: "9to5", wfh_frequency: "sometimes",
    budget_min: 12000, budget_max: 22000, shared_min: 2000, shared_max: 6000,
    preferred_city: user?.profile?.city || "",
    preferred_localities: [], move_in_date: "", room_preference: "private", gender_preference: "any",
    age_pref_min: 21, age_pref_max: 32, age_pref_none: false,
    ...(user?.profile || {}),
  });
  const [localityInput, setLocalityInput] = useState("");

  const upd = (k, v) => setP((s) => ({ ...s, [k]: v }));

  const toggleIntent = (k) => setIntents((s) => (s.includes(k) ? s.filter((x) => x !== k) : [...s, k]));

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      upd("photo_url", data.url);
      toast.success("Photo uploaded");
    } catch {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const addLocality = () => {
    const v = localityInput.trim();
    if (v && !p.preferred_localities.includes(v)) upd("preferred_localities", [...p.preferred_localities, v]);
    setLocalityInput("");
  };

  const steps = ["Intent", "About you", "Lifestyle", "Housing"];

  const canNext = () => {
    if (step === 0) return intents.length > 0;
    if (step === 1) return p.photo_url && p.first_name && p.age && p.gender && p.city;
    return true;
  };

  const toggleComfort = (v) => setP((s) => {
    const has = s.food_comfortable.includes(v);
    let next = has ? s.food_comfortable.filter((x) => x !== v) : [...s.food_comfortable, v];
    if (next.length === 0) next = [v]; // keep at least one
    return { ...s, food_comfortable: next };
  });

  const finish = async () => {
    setBusy(true);
    try {
      const occupation = p.role || (OPTIONS.sector.find((o) => o.v === p.sector)?.l) || (OPTIONS.employment_status.find((o) => o.v === p.employment_status)?.l) || "";
      const profile = { ...p, age: Number(p.age), occupation, preferred_city: p.preferred_city || p.city };
      const updated = await api.put("/profile", { intents, onboarded: true, profile });
      updateUser(updated.data);
      toast.success("You're all set! 🎉");
      navigate("/home");
    } catch {
      toast.error("Could not save profile");
    } finally {
      setBusy(false);
    }
  };

  const next = () => {
    if (step === 1 && !p.photo_url) { toast.error("Upload a photo or pick an avatar to continue"); return; }
    if (!canNext()) { toast.error("Please fill the required fields"); return; }
    if (step === 3) finish();
    else setStep(step + 1);
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <div className="sticky top-0 z-10 bg-background px-6 pt-6 pb-3">
        <img src="/logo.png" alt="FlatTribe" className="mb-3 h-8 w-auto" />
        <div className="flex gap-1.5">
          {steps.map((s, i) => (
            <div key={s} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= step ? "bg-brand" : "bg-border"}`} />
          ))}
        </div>
        <p className="mt-3 text-xs font-semibold uppercase tracking-widest text-brand">Step {step + 1} of 4</p>
      </div>

      <div className="flex-1 overflow-y-auto px-6 pb-32">
        {step === 0 && (
          <div className="ft-rise">
            <h2 className="font-heading text-3xl font-extrabold leading-tight text-ink">What brings you to FlatTribe?</h2>
            <p className="mt-1 text-ink-soft">Pick one or more. You can change this later.</p>
            <div className="mt-6 space-y-3">
              {INTENTS.map((it) => {
                const Icon = IconMap[it.icon];
                const active = intents.includes(it.key);
                return (
                  <button
                    key={it.key}
                    data-testid={`intent-${it.key}`}
                    onClick={() => toggleIntent(it.key)}
                    className={`flex w-full items-center gap-4 rounded-3xl border-2 p-5 text-left transition-all active:scale-[0.98] ${active ? "border-brand bg-brand-light" : "border-border bg-white"}`}
                  >
                    <div className={`grid h-12 w-12 shrink-0 place-items-center rounded-2xl ${active ? "bg-brand text-white" : "bg-secondary text-ink"}`}>
                      <Icon className="h-6 w-6" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-heading text-lg font-bold text-ink">{it.title}</h3>
                      <p className="text-sm text-ink-soft">{it.sub}</p>
                    </div>
                    {active && <div className="grid h-6 w-6 place-items-center rounded-full bg-brand text-white"><Check className="h-4 w-4" /></div>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="ft-rise space-y-5">
            <h2 className="font-heading text-3xl font-extrabold text-ink">Tell us about you</h2>

            <div className="rounded-3xl bg-brand-light/60 p-4">
              <h3 className="font-heading text-lg font-bold text-ink">Choose your profile</h3>
              <p className="mb-3 text-sm text-ink-soft">Help your future flatmates recognise you.</p>
              <div className="flex items-center gap-4">
                <div className="grid h-24 w-24 shrink-0 place-items-center overflow-hidden rounded-full bg-white ring-4 ring-white shadow-soft">
                  {p.photo_url ? <img src={resolveImg(p.photo_url)} alt="" className="h-full w-full object-cover" /> : (uploading ? <Loader2 className="h-6 w-6 animate-spin text-brand" /> : <Camera className="h-7 w-7 text-ink-soft" />)}
                </div>
                <label data-testid="onboarding-photo" className="flex-1 cursor-pointer rounded-2xl bg-brand px-4 py-3 text-center text-sm font-bold text-white shadow-brand">
                  {uploading ? "Uploading…" : "Upload a photo"}
                  <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
                </label>
              </div>
              <p className="mb-2 mt-4 text-xs font-bold uppercase tracking-wide text-ink-soft">Or pick an avatar</p>
              <div className="no-scrollbar flex gap-3 overflow-x-auto pb-1">
                {AVATARS.map((a, i) => (
                  <button key={a} type="button" data-testid={`ob-avatar-${i}`} onClick={() => upd("photo_url", a)}
                    className={`h-16 w-16 shrink-0 overflow-hidden rounded-full bg-white transition-all ${p.photo_url === a ? "ring-4 ring-brand" : "ring-2 ring-white"}`}>
                    <img src={a} alt="" className="h-full w-full object-cover" />
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Field label="First name"><Input data-testid="ob-firstname" value={p.first_name} onChange={(e) => upd("first_name", e.target.value)} className="h-12 rounded-2xl bg-white" /></Field>
              <Field label="Age"><Input data-testid="ob-age" type="number" value={p.age} onChange={(e) => upd("age", e.target.value)} className="h-12 rounded-2xl bg-white" /></Field>
            </div>
            <Field label="Gender">
              <div className="flex flex-wrap gap-2">
                {OPTIONS.gender.map((o) => <Chip key={o.v} active={p.gender === o.v} onClick={() => upd("gender", o.v)} testId={`ob-gender-${o.v}`}>{o.l}</Chip>)}
              </div>
            </Field>
            <Field label="Current status">
              <div className="flex flex-wrap gap-2">
                {OPTIONS.employment_status.map((o) => <Chip key={o.v} active={p.employment_status === o.v} onClick={() => upd("employment_status", o.v)} testId={`ob-status-${o.v}`}>{o.l}</Chip>)}
              </div>
            </Field>
            {(p.employment_status === "working" || p.employment_status === "self_employed") && (
              <Field label="Sector / Industry">
                <div className="flex flex-wrap gap-2">
                  {OPTIONS.sector.map((o) => <Chip key={o.v} active={p.sector === o.v} onClick={() => upd("sector", o.v)} testId={`ob-sector-${o.v}`}>{o.l}</Chip>)}
                </div>
              </Field>
            )}
            <Field label="Work mode">
              <div className="flex flex-wrap gap-2">
                {OPTIONS.work_mode.map((o) => <Chip key={o.v} active={p.work_mode === o.v} onClick={() => upd("work_mode", o.v)} testId={`ob-workmode-${o.v}`}>{o.l}</Chip>)}
              </div>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Role (optional)"><Input data-testid="ob-role" value={p.role} onChange={(e) => upd("role", e.target.value)} placeholder="Product Designer" className="h-12 rounded-2xl bg-white" /></Field>
              <Field label="Company / College"><Input value={p.company_college} onChange={(e) => upd("company_college", e.target.value)} placeholder="Razorpay" className="h-12 rounded-2xl bg-white" /></Field>
            </div>
            <Field label="City">
              <div className="flex flex-wrap gap-2">
                {CITIES.map((c) => <Chip key={c} active={p.city === c} onClick={() => { upd("city", c); upd("preferred_city", c); }} testId={`ob-city-${c}`}>{c}</Chip>)}
              </div>
            </Field>
            <Field label="Short bio"><Textarea data-testid="ob-bio" value={p.bio} onChange={(e) => upd("bio", e.target.value)} placeholder="A little about you & what you're looking for…" className="min-h-24 rounded-2xl bg-white" /></Field>
          </div>
        )}

        {step === 2 && (
          <div className="ft-rise space-y-5">
            <h2 className="font-heading text-3xl font-extrabold text-ink">Your lifestyle</h2>
            <p className="text-ink-soft">This helps us find your most compatible people.</p>
            {[
              ["wake_time", "When do you wake up?"],
              ["sleep_time", "When do you sleep?"],
              ["smoking", "Smoking"],
              ["drinking", "Drinking"],
              ["pets", "Pets"],
              ["social_lifestyle", "Social style"],
              ["noise_preference", "Noise at home"],
              ["guest_preference", "Guests over"],
            ].map(([key, label]) => (
              <Field key={key} label={label}>
                <div className="flex flex-wrap gap-2">
                  {OPTIONS[key].map((o) => <Chip key={o.v} active={p[key] === o.v} onClick={() => upd(key, o.v)} testId={`ob-${key}-${o.v}`}>{o.l}</Chip>)}
                </div>
              </Field>
            ))}
            <div className="rounded-2xl bg-brand-light/50 p-4 space-y-4">
              <Field label="What do you personally eat?">
                <div className="flex flex-wrap gap-2">
                  {OPTIONS.personal_food.map((o) => <Chip key={o.v} active={p.personal_food === o.v} onClick={() => upd("personal_food", o.v)} testId={`ob-personalfood-${o.v}`}>{o.l}</Chip>)}
                </div>
              </Field>
              <Field label="What are you comfortable with in the house?">
                <p className="-mt-1 mb-2 text-xs text-ink-soft">Select all that apply — this is different from what you personally eat.</p>
                <div className="flex flex-wrap gap-2">
                  {OPTIONS.food_comfortable.map((o) => <Chip key={o.v} active={p.food_comfortable.includes(o.v)} onClick={() => toggleComfort(o.v)} testId={`ob-comfort-${o.v}`}>{p.food_comfortable.includes(o.v) ? "✓ " : ""}{o.l}</Chip>)}
                </div>
              </Field>
            </div>
            <Field label={`Cleanliness — ${p.cleanliness}/5`}>
              <Slider data-testid="ob-cleanliness" value={[p.cleanliness]} min={1} max={5} step={1} onValueChange={(v) => upd("cleanliness", v[0])} className="py-2" />
            </Field>
          </div>
        )}

        {step === 3 && (
          <div className="ft-rise space-y-5">
            <h2 className="font-heading text-3xl font-extrabold text-ink">Housing & work</h2>
            <Field label="Work schedule">
              <div className="flex flex-wrap gap-2">{OPTIONS.work_schedule.map((o) => <Chip key={o.v} active={p.work_schedule === o.v} onClick={() => upd("work_schedule", o.v)}>{o.l}</Chip>)}</div>
            </Field>
            <Field label="Work from home">
              <div className="flex flex-wrap gap-2">{OPTIONS.wfh_frequency.map((o) => <Chip key={o.v} active={p.wfh_frequency === o.v} onClick={() => upd("wfh_frequency", o.v)}>{o.l}</Chip>)}</div>
            </Field>
            <Field label={`Monthly rent budget — ₹${p.budget_min.toLocaleString("en-IN")} to ₹${p.budget_max.toLocaleString("en-IN")}`}>
              <p className="-mt-1 mb-2 text-xs text-ink-soft">Rent only.</p>
              <div className="space-y-3 rounded-2xl bg-white p-4">
                <div><span className="text-xs text-ink-soft">Minimum</span><Slider value={[p.budget_min]} min={5000} max={60000} step={1000} onValueChange={(v) => upd("budget_min", v[0])} /></div>
                <div><span className="text-xs text-ink-soft">Maximum</span><Slider value={[p.budget_max]} min={5000} max={80000} step={1000} onValueChange={(v) => upd("budget_max", Math.max(v[0], p.budget_min))} /></div>
              </div>
            </Field>
            <Field label={`Shared household expense budget — ₹${p.shared_min.toLocaleString("en-IN")} to ₹${p.shared_max.toLocaleString("en-IN")}`}>
              <p className="-mt-1 mb-2 text-xs text-ink-soft">Maid, maintenance, furniture rental & other shared costs. Excludes your personal spending.</p>
              <div className="space-y-3 rounded-2xl bg-white p-4">
                <div><span className="text-xs text-ink-soft">Minimum</span><Slider data-testid="ob-shared-min" value={[p.shared_min]} min={0} max={15000} step={500} onValueChange={(v) => upd("shared_min", v[0])} /></div>
                <div><span className="text-xs text-ink-soft">Maximum</span><Slider data-testid="ob-shared-max" value={[p.shared_max]} min={0} max={20000} step={500} onValueChange={(v) => upd("shared_max", Math.max(v[0], p.shared_min))} /></div>
              </div>
            </Field>
            <Field label="Preferred flatmate age">
              <div className="rounded-2xl bg-white p-4">
                <label className="mb-3 flex items-center justify-between">
                  <span className="text-sm font-semibold text-ink">No age preference</span>
                  <input data-testid="ob-agepref-none" type="checkbox" checked={p.age_pref_none} onChange={(e) => upd("age_pref_none", e.target.checked)} className="h-5 w-5 accent-brand" />
                </label>
                {!p.age_pref_none && (
                  <>
                    <p className="mb-2 text-sm font-bold text-ink">{p.age_pref_min} — {p.age_pref_max} years</p>
                    <div className="space-y-3">
                      <div><span className="text-xs text-ink-soft">Minimum</span><Slider value={[p.age_pref_min]} min={18} max={60} step={1} onValueChange={(v) => upd("age_pref_min", Math.min(v[0], p.age_pref_max))} /></div>
                      <div><span className="text-xs text-ink-soft">Maximum</span><Slider value={[p.age_pref_max]} min={18} max={60} step={1} onValueChange={(v) => upd("age_pref_max", Math.max(v[0], p.age_pref_min))} /></div>
                    </div>
                  </>
                )}
              </div>
            </Field>
            <Field label="Preferred city">
              <div className="flex flex-wrap gap-2">{CITIES.map((c) => <Chip key={c} active={p.preferred_city === c} onClick={() => upd("preferred_city", c)}>{c}</Chip>)}</div>
            </Field>
            <Field label="Preferred localities">
              <div className="flex gap-2">
                <Input data-testid="ob-locality-input" value={localityInput} onChange={(e) => setLocalityInput(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addLocality())} placeholder="e.g. Koramangala" className="h-11 rounded-2xl bg-white" />
                <Button type="button" onClick={addLocality} className="h-11 rounded-2xl bg-ink px-4 text-white">Add</Button>
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {p.preferred_localities.map((l) => (
                  <span key={l} onClick={() => upd("preferred_localities", p.preferred_localities.filter((x) => x !== l))} className="cursor-pointer rounded-full bg-brand-light px-3 py-1 text-sm font-semibold text-brand-dark">{l} ✕</span>
                ))}
              </div>
            </Field>
            <Field label="Move-in date"><Input data-testid="ob-movein" type="date" value={p.move_in_date} onChange={(e) => upd("move_in_date", e.target.value)} className="h-12 rounded-2xl bg-white" /></Field>
            <Field label="Room preference">
              <div className="flex flex-wrap gap-2">{OPTIONS.room_preference.map((o) => <Chip key={o.v} active={p.room_preference === o.v} onClick={() => upd("room_preference", o.v)}>{o.l}</Chip>)}</div>
            </Field>
          </div>
        )}
      </div>

      <div className="fixed bottom-0 left-1/2 w-full max-w-[480px] -translate-x-1/2 border-t border-gray-100 bg-white/95 px-6 py-4 backdrop-blur-xl">
        <div className="flex gap-3">
          {step > 0 && <Button type="button" variant="outline" onClick={() => setStep(step - 1)} className="h-12 rounded-full border-border px-6 font-bold">Back</Button>}
          <Button data-testid="onboarding-next" type="button" onClick={next} disabled={busy} className="h-12 flex-1 rounded-full bg-brand font-bold text-white shadow-brand hover:bg-brand-dark">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : step === 3 ? "Finish & explore" : <>Continue <ChevronRight className="ml-1 h-5 w-5" /></>}
          </Button>
        </div>
      </div>
    </div>
  );
}
